/**
 * tile-forms — per-type param forms for the add-tile picker.
 *
 * Each form collects type-specific params, validates inline via param-forms.ts,
 * then calls onSubmit with a parsed Tile. Calm inline error text (sentence case,
 * no exclamation marks). Forms are self-contained; AddTileDrawer imports them.
 */
import React, { useState, useEffect, useRef } from 'react';
import { SegmentedControl } from '../components/SegmentedControl.js';
import { TILE_REGISTRY, parseTile, TILE_SCHEMA_VERSION, type TileType } from 'briefpane/schema';
import type { Tile } from 'briefpane/schema';
import { generateTileId } from '../edit-mode/generate-tile-id.js';
import {
  validateWeatherForm,
  validateNewsForm,
  validateMarketsForm,
  validateSportsForm,
  validateClockForm,
  validateCalendarForm,
  validateNoteForm,
  validateTodoForm,
  validateCountdownForm,
  validateRssForm,
  rssUrlErrorMessage,
  weatherFormDefaults,
  newsFormDefaults,
  marketsFormDefaults,
  sportsFormDefaults,
  clockFormDefaults,
  calendarFormDefaults,
  noteFormDefaults,
  todoFormDefaults,
  countdownFormDefaults,
  rssFormDefaults,
  NEWS_TOPICS,
  SPORTS_LEAGUES,
  MAX_NOTE_CHARS,
  MAX_RSS_ITEMS,
  type WeatherFormValues,
  type NewsFormValues,
  type MarketsFormValues,
  type SportsFormValues,
  type ClockFormValues,
  type CalendarFormValues,
  type NoteFormValues,
  type CountdownFormValues,
  type RssFormValues,
  type GeoSuggestion,
} from './param-forms.js';

export const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: 'var(--text-label)',
  color: 'var(--color-text-secondary)',
  fontWeight: 500,
};

export const inputStyle: React.CSSProperties = {
  marginTop: '4px',
  padding: '6px 8px',
  fontSize: 'var(--text-detail)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-input)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-button)',
  width: '100%',
  boxSizing: 'border-box',
};

/** Error text element — calm, sentence case, no exclamation marks. */
export function FieldError({ msg }: { msg: string | undefined }): React.ReactElement | null {
  if (!msg) return null;
  return (
    <span
      role="alert"
      style={{
        fontSize: 'var(--text-label)',
        color: 'var(--color-negative-accent)',
        display: 'block',
        marginTop: '2px',
      }}
    >
      {msg}
    </span>
  );
}

export function SubmitButton(): React.ReactElement {
  return (
    <button
      type="submit"
      style={{
        background: 'var(--color-accent)',
        border: 'none',
        borderRadius: 'var(--radius-button)',
        color: '#fff',
        fontSize: 'var(--text-detail)',
        fontWeight: 500,
        padding: '8px 20px',
        cursor: 'pointer',
        alignSelf: 'flex-start',
        marginTop: 'var(--space-2)',
      }}
    >
      Add tile
    </button>
  );
}

/** Default glance map per tile type. */
const GLANCE_MAPS: Record<string, { primary: string; delta: string | null; state: string | null }> = {
  'weather.current': { primary: 'tempF', delta: 'vsYesterdayF', state: 'condition' },
  'news.headlines': { primary: 'headline', delta: 'source', state: null },
  'markets.quote': { primary: 'price', delta: 'changePct', state: 'direction' },
  'sports.score': { primary: 'score', delta: 'status', state: null },
  'clock.local': { primary: 'time', delta: 'date', state: null },
  'calendar.peek': { primary: 'nextEventTitle', delta: 'minutesUntilNext', state: 'allDayCount' },
  // Local utility widgets (D-015) — default glance maps per spec:
  'note.sticky': { primary: 'preview', delta: null, state: null },
  'todo.list': { primary: 'remainingCount', delta: 'nextItemText', state: null },
  'countdown.event': { primary: 'daysLeft', delta: 'targetLabel', state: null },
  // User-source (ADR-005): headline = latest item title (≤8 chars shown on glance)
  'rss.feed': { primary: 'headline', delta: 'source', state: null },
};

/** Build a valid Tile from type + params. Returns null on schema parse failure. */
export function buildTile(type: TileType, params: Record<string, unknown>): Tile | null {
  const def = TILE_REGISTRY[type];
  const id = generateTileId();
  const defaultSize = def.allowedSizes[0] ?? '1x1';
  const defaultVariant = def.allowedVariants[0] ?? 'big-number';
  const glance = GLANCE_MAPS[type] ?? { primary: '', delta: null, state: null };

  const rawTile: Record<string, unknown> = {
    v: TILE_SCHEMA_VERSION,
    id,
    type,
    display: { size: defaultSize, variant: defaultVariant },
    glance,
  };

  if (def.noFetch) {
    rawTile['source'] = { provider: 'local', params };
  } else if (def.allowedProviders.length > 0) {
    rawTile['source'] = { provider: def.allowedProviders[0], params };
    rawTile['refresh'] = { intervalSec: def.ttlSecFloor };
  }

  try { return parseTile(rawTile); } catch { return null; }
}

/* ---- Per-type forms ---- */

/** Debounce hook — returns a debounced copy of `value` after `delayMs`. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function WeatherForm({ onSubmit }: { onSubmit: (tile: Tile) => void }): React.ReactElement {
  const [values, setValues] = useState<WeatherFormValues>(weatherFormDefaults);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [geoCoords, setGeoCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'done' | 'denied'>('idle');

  // City search state
  const [cityQuery, setCityQuery] = useState('');
  const [citySuggestions, setCitySuggestions] = useState<GeoSuggestion[]>([]);
  const [citySearching, setCitySearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncedCityQuery = useDebounced(cityQuery, 400);
  const cityInputRef = useRef<HTMLInputElement>(null);

  // Fetch city suggestions from /api/geo when the debounced query changes
  useEffect(() => {
    const q = debouncedCityQuery.trim();
    if (q.length < 2) { setCitySuggestions([]); return; }
    let cancelled = false;
    setCitySearching(true);
    fetch(`/api/geo?q=${encodeURIComponent(q)}`)
      .then((r) => r.json() as Promise<{ results?: GeoSuggestion[] }>)
      .then((data) => {
        if (!cancelled) {
          setCitySuggestions(Array.isArray(data.results) ? data.results.slice(0, 5) : []);
          setShowSuggestions(true);
        }
      })
      .catch(() => { if (!cancelled) setCitySuggestions([]); })
      .finally(() => { if (!cancelled) setCitySearching(false); });
    return () => { cancelled = true; };
  }, [debouncedCityQuery]);

  function handleCitySelect(s: GeoSuggestion): void {
    setValues((v) => ({
      ...v,
      useCurrentLocation: false,
      lat: String(s.lat),
      lon: String(s.lon),
    }));
    setCityQuery(`${s.name}${s.state ? `, ${s.state}` : ''}, ${s.country}`);
    setCitySuggestions([]);
    setShowSuggestions(false);
  }

  function requestGeo(): void {
    setGeoStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeoCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setGeoStatus('done'); },
      () => { setGeoStatus('denied'); setValues((v) => ({ ...v, useCurrentLocation: false })); },
      { timeout: 5000 },
    );
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const result = validateWeatherForm(values, geoCoords?.lat, geoCoords?.lon);
    if (!result.ok) { setErrors(result.errors); return; }
    setErrors({});
    const tile = buildTile('weather.current', result.params as Record<string, unknown>);
    if (tile) onSubmit(tile);
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {/* City search — debounced 400ms, results up to 5 */}
      <div style={{ position: 'relative' }}>
        <label style={labelStyle}>Search by city
          <input
            ref={cityInputRef}
            style={inputStyle}
            type="text"
            value={cityQuery}
            onChange={(e) => { setCityQuery(e.target.value); setShowSuggestions(false); }}
            onFocus={() => { if (citySuggestions.length > 0) setShowSuggestions(true); }}
            placeholder="e.g. London, Paris, Tokyo"
            autoComplete="off"
          />
        </label>
        {citySearching && (
          <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-dim)', display: 'block', marginTop: 2 }}>
            Searching…
          </span>
        )}
        {showSuggestions && citySuggestions.length > 0 && (
          <ul
            role="listbox"
            aria-label="City suggestions"
            style={{
              position: 'absolute',
              zIndex: 10,
              top: '100%',
              left: 0,
              right: 0,
              listStyle: 'none',
              margin: 0,
              padding: 0,
              background: 'var(--color-bg-input)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-button)',
              overflow: 'hidden',
            }}
          >
            {citySuggestions.map((s, i) => (
              <li key={i}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => handleCitySelect(s)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'var(--text-detail)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {s.name}{s.state ? `, ${s.state}` : ''}, {s.country}
                </button>
              </li>
            ))}
          </ul>
        )}
        {debouncedCityQuery.trim().length >= 2 && !citySearching && citySuggestions.length === 0 && showSuggestions && (
          <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-dim)', display: 'block', marginTop: 2 }}>
            No cities found — enter coordinates below
          </span>
        )}
      </div>
      <label style={labelStyle}>
        <input type="checkbox" checked={values.useCurrentLocation}
          onChange={(e) => {
            const checked = e.target.checked;
            setValues((v) => ({ ...v, useCurrentLocation: checked }));
            if (checked && geoStatus === 'idle') requestGeo();
          }} />
        {' '}Use current location
        {geoStatus === 'loading' && <span style={{ color: 'var(--color-text-dim)', marginLeft: 8 }}>Getting location…</span>}
        {geoStatus === 'denied' && <span style={{ color: 'var(--color-alert-accent)', marginLeft: 8 }}>Location denied — enter manually</span>}
      </label>
      {(!values.useCurrentLocation || geoStatus === 'denied') && (
        <>
          <div>
            <label style={labelStyle}>Latitude
              <input style={inputStyle} type="text" value={values.lat}
                onChange={(e) => setValues((v) => ({ ...v, lat: e.target.value }))} placeholder="-90 to 90" />
            </label>
            <FieldError msg={errors['lat']} />
          </div>
          <div>
            <label style={labelStyle}>Longitude
              <input style={inputStyle} type="text" value={values.lon}
                onChange={(e) => setValues((v) => ({ ...v, lon: e.target.value }))} placeholder="-180 to 180" />
            </label>
            <FieldError msg={errors['lon']} />
          </div>
        </>
      )}
      <div>
        <label style={labelStyle}>Units
          <select style={inputStyle} value={values.units}
            onChange={(e) => setValues((v) => ({ ...v, units: e.target.value as 'metric' | 'imperial' }))}>
            <option value="imperial">Imperial (°F)</option>
            <option value="metric">Metric (°C)</option>
          </select>
        </label>
      </div>
      <FieldError msg={errors['_']} />
      <SubmitButton />
    </form>
  );
}

export function NewsForm({ onSubmit }: { onSubmit: (tile: Tile) => void }): React.ReactElement {
  const [values, setValues] = useState<NewsFormValues>(newsFormDefaults);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const result = validateNewsForm(values);
    if (!result.ok) { setErrors(result.errors); return; }
    setErrors({});
    const tile = buildTile('news.headlines', result.params as Record<string, unknown>);
    if (tile) onSubmit(tile);
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div>
        <label style={labelStyle}>Topic
          <select style={inputStyle} value={values.topic}
            onChange={(e) => setValues((v) => ({ ...v, topic: e.target.value as typeof values.topic }))}>
            {NEWS_TOPICS.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </label>
      </div>
      <div>
        <label style={labelStyle}>Number of headlines
          <input style={inputStyle} type="number" min="1" max="8" value={values.count}
            onChange={(e) => setValues((v) => ({ ...v, count: e.target.value }))} />
        </label>
        <FieldError msg={errors['count']} />
      </div>
      <div>
        <label style={labelStyle}>
          Keyword (optional — narrows to matching stories)
          <input
            style={inputStyle}
            type="text"
            value={values.query}
            maxLength={60}
            placeholder="e.g. climate, AI, elections"
            onChange={(e) => setValues((v) => ({ ...v, query: e.target.value }))}
          />
        </label>
        <FieldError msg={errors['query']} />
      </div>
      <FieldError msg={errors['_']} />
      <SubmitButton />
    </form>
  );
}

export function MarketsForm({ onSubmit }: { onSubmit: (tile: Tile) => void }): React.ReactElement {
  const [values, setValues] = useState<MarketsFormValues>(marketsFormDefaults);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const result = validateMarketsForm(values);
    if (!result.ok) { setErrors(result.errors); return; }
    setErrors({});
    const tile = buildTile('markets.quote', result.params as Record<string, unknown>);
    if (tile) onSubmit(tile);
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div>
        <label style={labelStyle}>Symbols (comma-separated, e.g. AAPL,MSFT)
          <input style={inputStyle} type="text" value={values.symbolsRaw}
            onChange={(e) => setValues((v) => ({ ...v, symbolsRaw: e.target.value }))} placeholder="SPY,AAPL" />
        </label>
        <FieldError msg={errors['symbolsRaw']} />
      </div>
      <div>
        <span style={{ ...labelStyle, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>Display</span>
          <SegmentedControl
            ariaLabel="Display mode"
            options={[{ value: 'price', label: 'Price' }, { value: 'change', label: 'Change %' }]}
            value={values.display}
            onChange={(v) => setValues((prev) => ({ ...prev, display: v as 'price' | 'change' }))}
          />
        </span>
      </div>
      <FieldError msg={errors['_']} />
      <SubmitButton />
    </form>
  );
}

export function SportsForm({ onSubmit }: { onSubmit: (tile: Tile) => void }): React.ReactElement {
  const [values, setValues] = useState<SportsFormValues>(sportsFormDefaults);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const result = validateSportsForm(values);
    if (!result.ok) { setErrors(result.errors); return; }
    setErrors({});
    const tile = buildTile('sports.score', result.params as Record<string, unknown>);
    if (tile) onSubmit(tile);
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div>
        <span style={{ ...labelStyle, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>League</span>
          <SegmentedControl
            ariaLabel="Sports league"
            options={SPORTS_LEAGUES.map((l) => ({ value: l, label: l.toUpperCase() }))}
            value={values.league}
            onChange={(v) => setValues((prev) => ({ ...prev, league: v as typeof values.league }))}
          />
        </span>
      </div>
      <div>
        <label style={labelStyle}>Team ID
          <input style={inputStyle} type="text" value={values.teamId}
            onChange={(e) => setValues((v) => ({ ...v, teamId: e.target.value }))} placeholder="patriots" />
        </label>
        <FieldError msg={errors['teamId']} />
      </div>
      <FieldError msg={errors['_']} />
      <SubmitButton />
    </form>
  );
}

export function ClockForm({ onSubmit }: { onSubmit: (tile: Tile) => void }): React.ReactElement {
  const [values, setValues] = useState<ClockFormValues>(clockFormDefaults);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const result = validateClockForm(values);
    if (!result.ok) { setErrors(result.errors); return; }
    setErrors({});
    const tile = buildTile('clock.local', result.params as Record<string, unknown>);
    if (tile) onSubmit(tile);
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div>
        <label style={labelStyle}>Timezone (IANA, e.g. America/New_York)
          <input style={inputStyle} type="text" value={values.tz}
            onChange={(e) => setValues((v) => ({ ...v, tz: e.target.value }))} placeholder="America/New_York" />
        </label>
        <FieldError msg={errors['tz']} />
      </div>
      <div>
        <span style={{ ...labelStyle, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>Format</span>
          <SegmentedControl
            ariaLabel="Clock format"
            options={[{ value: '12h', label: '12-hour' }, { value: '24h', label: '24-hour' }]}
            value={values.format}
            onChange={(v) => setValues((prev) => ({ ...prev, format: v as '12h' | '24h' }))}
          />
        </span>
      </div>
      <FieldError msg={errors['_']} />
      <SubmitButton />
    </form>
  );
}

export function CalendarForm({ onSubmit }: { onSubmit: (tile: Tile) => void }): React.ReactElement {
  const [values, setValues] = useState<CalendarFormValues>(calendarFormDefaults);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const result = validateCalendarForm(values);
    if (!result.ok) { setErrors(result.errors); return; }
    setErrors({});
    // calendarId is fixed to 'primary' (ADR-004 §v1.5 constraint)
    const tile = buildTile('calendar.peek', result.params as Record<string, unknown>);
    if (tile) onSubmit(tile);
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {/* calendarId is fixed in v1.5 — shown as informational text only */}
      <div>
        <span style={{ ...labelStyle, display: 'block' }}>Calendar</span>
        <span style={{
          display: 'block',
          marginTop: '4px',
          padding: '6px 8px',
          fontSize: 'var(--text-detail)',
          color: 'var(--color-text-dim)',
          backgroundColor: 'var(--color-bg-input)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-button)',
        }}>
          Primary calendar (Google)
        </span>
      </div>
      <div>
        <label style={labelStyle}>Events to show
          <input style={inputStyle} type="number" min="1" max="10" value={values.maxEvents}
            onChange={(e) => setValues((v) => ({ ...v, maxEvents: e.target.value }))} />
        </label>
        <FieldError msg={errors['maxEvents']} />
      </div>
      <div>
        <label style={labelStyle}>Look ahead (hours)
          <input style={inputStyle} type="number" min="1" max="48" value={values.lookaheadHours}
            onChange={(e) => setValues((v) => ({ ...v, lookaheadHours: e.target.value }))} />
        </label>
        <FieldError msg={errors['lookaheadHours']} />
      </div>
      <FieldError msg={errors['_']} />
      <p style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-dim)', margin: '0' }}>
        You'll connect your Google account after adding the tile.
      </p>
      <SubmitButton />
    </form>
  );
}

export function NoteForm({ onSubmit }: { onSubmit: (tile: Tile) => void }): React.ReactElement {
  const [values, setValues] = useState<NoteFormValues>(noteFormDefaults);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const result = validateNoteForm(values);
    if (!result.ok) { setErrors(result.errors); return; }
    setErrors({});
    const tile = buildTile('note.sticky', result.params as Record<string, unknown>);
    if (tile) onSubmit(tile);
  }

  const charCount = values.text.length;
  const overLimit = charCount > MAX_NOTE_CHARS;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div>
        <label style={labelStyle}>Note text (optional — you can write after adding)
          <textarea
            style={{ ...inputStyle, height: '100px', resize: 'vertical', fontFamily: 'inherit' }}
            value={values.text}
            maxLength={MAX_NOTE_CHARS}
            onChange={(e) => setValues((v) => ({ ...v, text: e.target.value }))}
            placeholder="Start writing..."
          />
        </label>
        <div style={{
          fontSize: 'var(--text-label)',
          color: overLimit ? 'var(--color-negative-accent)' : 'var(--color-text-dim)',
          textAlign: 'right',
          marginTop: '2px',
        }}>
          {charCount} / {MAX_NOTE_CHARS}
        </div>
        <FieldError msg={errors['text']} />
      </div>
      <FieldError msg={errors['_']} />
      <SubmitButton />
    </form>
  );
}

export function TodoForm({ onSubmit }: { onSubmit: (tile: Tile) => void }): React.ReactElement {
  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const result = validateTodoForm(todoFormDefaults);
    if (!result.ok) return;
    const tile = buildTile('todo.list', result.params as Record<string, unknown>);
    if (tile) onSubmit(tile);
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <p style={{ fontSize: 'var(--text-detail)', color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.5 }}>
        Creates an empty to-do list. Add items by tapping the tile to open the detail view.
      </p>
      <SubmitButton />
    </form>
  );
}

export function CountdownForm({ onSubmit }: { onSubmit: (tile: Tile) => void }): React.ReactElement {
  const [values, setValues] = useState<CountdownFormValues>(() => countdownFormDefaults());
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const result = validateCountdownForm(values);
    if (!result.ok) { setErrors(result.errors); return; }
    setErrors({});
    const tile = buildTile('countdown.event', result.params as Record<string, unknown>);
    if (tile) onSubmit(tile);
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div>
        <label style={labelStyle}>Event label (e.g. Launch day)
          <input
            style={inputStyle}
            type="text"
            value={values.label}
            maxLength={40}
            onChange={(e) => setValues((v) => ({ ...v, label: e.target.value }))}
            placeholder="Launch day"
          />
        </label>
        <FieldError msg={errors['label']} />
      </div>
      <div>
        <label style={labelStyle}>Target date and time
          <input
            style={inputStyle}
            type="datetime-local"
            value={values.targetLocal}
            onChange={(e) => setValues((v) => ({ ...v, targetLocal: e.target.value }))}
          />
        </label>
        <FieldError msg={errors['targetLocal']} />
      </div>
      <p style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-dim)', margin: 0 }}>
        Past dates are allowed — the tile switches to a "days since" mode.
      </p>
      <FieldError msg={errors['_']} />
      <SubmitButton />
    </form>
  );
}

/**
 * RssForm — add-tile form for rss.feed (ADR-005 user-source tiles).
 *
 * URL validated live against userSourceUrlSchema with per-failure calm messages:
 *   - http:// → "https only — http feeds are not supported"
 *   - credentials → "Credentials are not allowed in feed URLs"
 *   - private host → "That address looks private — use a public feed URL"
 *   - empty → shows no error until submit
 *
 * PRIVACY: feedUrl is stored in the board config as a private user param. It
 * is never emitted to any analytics event (ADR-005 §privacy). Only the hostname
 * is shown in the detail sheet (RssDetail).
 *
 * Tile is created only on a valid URL — blank URL submits do not create tiles.
 */
export function RssForm({ onSubmit }: { onSubmit: (tile: Tile) => void }): React.ReactElement {
  const [values, setValues] = useState<RssFormValues>(rssFormDefaults);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Live URL error — shown while typing once there is content
  const liveUrlError = values.feedUrl ? rssUrlErrorMessage(values.feedUrl) : null;

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const result = validateRssForm(values);
    if (!result.ok) { setErrors(result.errors); return; }
    setErrors({});
    const tile = buildTile('rss.feed', result.params as Record<string, unknown>);
    if (tile) onSubmit(tile);
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div>
        <label style={labelStyle}>
          Feed URL
          <input
            style={{
              ...inputStyle,
              borderColor: liveUrlError ? 'var(--color-negative-accent)' : undefined,
            }}
            type="url"
            value={values.feedUrl}
            maxLength={300}
            placeholder="https://example.com/feed"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => {
              setValues((v) => ({ ...v, feedUrl: e.target.value }));
              // Clear submit-time error once user starts editing
              if (errors['feedUrl']) setErrors((prev) => { const next = { ...prev }; delete next['feedUrl']; return next; });
            }}
          />
        </label>
        {/* Show live error while typing; fall back to submit-time error */}
        <FieldError msg={liveUrlError ?? errors['feedUrl']} />
      </div>
      <div>
        <label style={labelStyle}>Items to show (1–{MAX_RSS_ITEMS})
          <input
            style={inputStyle}
            type="number"
            min="1"
            max={MAX_RSS_ITEMS}
            value={values.maxItems}
            onChange={(e) => setValues((v) => ({ ...v, maxItems: e.target.value }))}
          />
        </label>
        <FieldError msg={errors['maxItems']} />
      </div>
      {/* ADR-005 PRIVACY hint */}
      <p style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-dim)', margin: 0, lineHeight: 1.5 }}>
        Your feed address is stored in your board and treated as private.
      </p>
      <FieldError msg={errors['_']} />
      <SubmitButton />
    </form>
  );
}
